import { Store } from '@tanstack/store'
import { captureError } from '@/lib/sentry'

/**
 * Token data stored per lead ID
 */
interface TokenData {
  token: string
  expiresAt: number // Unix timestamp in milliseconds
}

/**
 * Store structure: mapping of lead IDs to their token data
 */
interface TokenStoreState {
  tokens: Record<string, TokenData>
}

const STORAGE_KEY = 'cortado_portal_tokens'

export type PortalTokenStatus = 'missing' | 'valid' | 'expired' | 'unattributable'

/**
 * Decode a JWT payload to a generic object.
 * Returns null when the token is not a parsable JWT.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length < 3) {
      return null
    }
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = base64.length % 4
    if (pad) {
      base64 += '='.repeat(4 - pad)
    }
    return JSON.parse(atob(base64)) as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * Tokens issued before the "recipient contact attribution" change lack
 * `recipient_contact_id`. Quote viewing can still send these tokens for
 * anonymous fallback access, but protected flows treat them as invalid.
 */
export function tokenHasRequiredClaims(token: string): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload) {
    return false
  }
  const recipientContactId = payload.recipient_contact_id
  if (payload.attribution_mode === 'anonymous') {
    return typeof recipientContactId !== 'string' || recipientContactId.length === 0
  }
  return typeof recipientContactId === 'string' && recipientContactId.length > 0
}

/**
 * Load tokens from localStorage. Keep raw tokens even when expired or missing
 * recipient claims so quote pages can send them for anonymous fallback access.
 * Strict callers still use getToken(), which rejects these tokens.
 */
function loadFromStorage(): TokenStoreState {
  if (typeof localStorage === 'undefined') {
    return { tokens: {} }
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return { tokens: {} }
    }
    const parsed = JSON.parse(stored) as TokenStoreState
    const tokens: Record<string, TokenData> = {}
    Object.entries(parsed.tokens ?? {}).forEach(([leadId, data]) => {
      if (data?.token && typeof data.expiresAt === 'number') {
        tokens[leadId] = data
      }
    })
    return { tokens }
  } catch (error) {
    console.error('Failed to load tokens from localStorage:', error)
    captureError(error instanceof Error ? error : new Error(String(error)), { context: 'token-store', action: 'load-from-storage' })
    return { tokens: {} }
  }
}

/**
 * Save tokens to localStorage
 */
function saveToStorage(state: TokenStoreState): void {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error('Failed to save tokens to localStorage:', error)
    captureError(error instanceof Error ? error : new Error(String(error)), { context: 'token-store', action: 'save-to-storage' })
  }
}

/**
 * Parse JWT token to extract expiry time
 */
export function getTokenExpiry(token: string): number {
  try {
    const parts = token.split('.')
    if (parts.length < 3) {
      throw new Error('Token is not a valid JWT format')
    }
    // JWT payloads use base64url encoding; convert to standard base64 for atob()
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const pad = base64.length % 4
    if (pad) {
      base64 += '='.repeat(4 - pad)
    }
    const payload = JSON.parse(atob(base64))
    return payload.exp * 1000 // Convert to milliseconds
  } catch (error) {
    console.error('Failed to parse token expiry:', error)
    captureError(error instanceof Error ? error : new Error(String(error)), { context: 'token-store', action: 'parse-token-expiry' })
    // Default to 24 hours from now if parsing fails
    return Date.now() + 24 * 60 * 60 * 1000
  }
}

/**
 * Create the token store with localStorage persistence
 */
export const tokenStore = new Store<TokenStoreState>(loadFromStorage(), {
  onUpdate: () => {
    saveToStorage(tokenStore.state)
  },
})

/**
 * Get token for a specific lead ID.
 * Returns null if the token doesn't exist, is expired, or is missing the
 * claims required by protected portal flows.
 */
export function getToken(leadId: string): string | null {
  const state = tokenStore.state
  const tokenData = state.tokens[leadId]

  if (!tokenData) {
    return null
  }

  // Check if expired
  if (tokenData.expiresAt <= Date.now()) {
    return null
  }

  // Drop tokens issued before the current backend claim requirements.
  if (!tokenHasRequiredClaims(tokenData.token)) {
    return null
  }

  return tokenData.token
}

/**
 * Get the raw stored token without local validity gates.
 * Use this only for quote rendering fallback paths where the backend can
 * safely decide whether to render anonymously.
 */
export function getStoredToken(resourceId: string): string | null {
  return tokenStore.state.tokens[resourceId]?.token || null
}

/**
 * Describe local token validity without evicting it.
 * Valid tokens are likely attributable; all other statuses should render quote
 * access messaging when the backend allows an anonymous quote load.
 */
export function getTokenStatus(resourceId: string): PortalTokenStatus {
  const tokenData = tokenStore.state.tokens[resourceId]
  if (!tokenData?.token) {
    return 'missing'
  }

  if (tokenData.expiresAt <= Date.now()) {
    return 'expired'
  }

  if (!tokenHasRequiredClaims(tokenData.token)) {
    return 'unattributable'
  }

  return 'valid'
}

/**
 * Store token for a specific lead ID with automatic expiry detection
 */
export function setToken(leadId: string, token: string): void {
  const expiresAt = getTokenExpiry(token)

  tokenStore.setState((state) => ({
    tokens: {
      ...state.tokens,
      [leadId]: { token, expiresAt },
    },
  }))
}

/**
 * Remove token for a specific lead ID
 */
export function removeToken(leadId: string): void {
  tokenStore.setState((state) => {
    const { [leadId]: _, ...remainingTokens } = state.tokens
    return { tokens: remainingTokens }
  })
}

/**
 * Check if a valid token exists for a lead ID
 */
export function isTokenValid(leadId: string): boolean {
  return getToken(leadId) !== null
}

/**
 * Clear all tokens (useful for debugging or logout)
 */
export function clearAllTokens(): void {
  tokenStore.setState({ tokens: {} })
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY)
  }
}

/**
 * Associate an additional ID with an existing token
 * This is useful when a quote is accepted and we need to access the booking
 * using the same token that was originally tied to the inquiry/lead ID.
 *
 * @param sourceId - The ID that already has a token stored (e.g., inquiry ID)
 * @param targetId - The new ID to associate with the same token (e.g., booking ID)
 * @returns true if the token was successfully copied, false otherwise
 */
export function associateToken(sourceId: string, targetId: string): boolean {
  const token = getToken(sourceId)
  if (!token) {
    return false
  }

  // Store the same token under the new ID
  setToken(targetId, token)
  return true
}
